import React, { useEffect, useRef, useState } from "react";
import { Logo } from "../../atoms/Logo";
import {
  Box,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Stack,
  VStack,
  Link,
  Spinner,
  Center,
  useDisclosure,
} from "@chakra-ui/react";
import { fetchAllProducts } from "../../../queries/fetchAllProducts";
import ProductCard from "../ProductCard";
import { useLazyQuery } from "@apollo/client";
import SEARCH_QUERY from "../../../queries/searchQuery";
import MobileDrawer from "../MobileDrawer";
import useAuth from "../../useAuth";
import {
  brandsToExclude,
  categoriesToExclude,
} from "../../../queries/toExclude";
import NoResults from "../NoResults";
import { HiDotsVertical, MdClose, MdSearch } from "../../customIcons";

const SmallResolutionHeader = () => {
  const { loggedIn, isUnlicensedUser, isCustomerDefault, isBankAcc } =
    useAuth();
  const mobileNavbar = useDisclosure();
  // QUERY
  const [query, setQuery] = useState("");
  const [isSearchOpen, setSearchOpen] = useState(false);

  const [executeSearch, { loading, data }] = useLazyQuery(SEARCH_QUERY);

  // ALL VARIABLES FOR POSTS, BRANDS, CATS...
  const products = fetchAllProducts();
  const postsData = data?.posts?.nodes;
  const brandsData = data?.tags?.nodes ?? [];
  const categoriesData = data?.categories?.nodes;

  // LOGIC FOR SHOWING SEARCH RESULTS
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (showDropdown) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [showDropdown]);

  // CLOSE COMPONENT ON CLICK OUTSIDE
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  // MIN 3 CHARACTERS TO SHOW SEARCH RESULTS
  const handleInputChange = (e: any) => {
    const inputValue = e.target.value;
    if (inputValue.length >= 3) {
      setQuery(inputValue);
      executeSearch({
        variables: {
          search: inputValue,
        },
      });
      if (!showDropdown) {
        setShowDropdown(true);
      }
    } else {
      setQuery(inputValue);
      setShowDropdown(false);
    }
  };

  const toggleSearch = () => {
    setSearchOpen(!isSearchOpen);
    setShowDropdown(!showDropdown);
  };

  // BRANDS TO EXLUDE
  const filteredBrands = React.useMemo(() => {
    if (isUnlicensedUser || !loggedIn || isCustomerDefault || isBankAcc) {
      return brandsData?.filter((item: any) => {
        return !brandsToExclude.includes(item.name);
      });
    }

    return brandsData?.filter((item: any) => {
      return (
        item.name !== "Unlicensed" &&
        item.name !== "Medical Devices" &&
        item.name !== "Pharmaceuticals"
      );
    });
  }, [isUnlicensedUser, loggedIn, isCustomerDefault, isBankAcc, brandsData]);

  // CATS TO EXCLUDE
  const relatedProductCategoryName = "Related Product";
  const filteredCategories = React.useMemo(() => {
    if (isUnlicensedUser || !loggedIn || isCustomerDefault || isBankAcc) {
      return categoriesData?.filter((item: any) => {
        return (
          !categoriesToExclude.includes(item.name) &&
          item.name !== relatedProductCategoryName &&
          item.name !== "Recommended Products" &&
          item.name !== "Best Selling Product"
        );
      });
    }

    return categoriesData?.filter(
      (item: any) =>
        item.name !== relatedProductCategoryName &&
        item.name !== "Recommended Products" &&
        item.name !== "Best Selling Product",
    );
  }, [isUnlicensedUser, categoriesData, categoriesToExclude]);

  // PRODUCTS TO EXCLUDE
  const filteredProducts = React.useMemo(() => {
    let allProducts = products;
    if (isUnlicensedUser || !loggedIn || isCustomerDefault || isBankAcc) {
      allProducts = products.filter((item: any) => {
        return item.productTags?.nodes.every(
          (tag: any) =>
            tag.name !== "Unlicensed" && tag.name !== "Pharmaceuticals",
        );
      });
    }

    return allProducts.filter(
      (prod: any) =>
        prod.name.toLowerCase().includes(query.toLowerCase()) ||
        prod.name.toUpperCase().includes(query),
    );
  }, [isUnlicensedUser, isCustomerDefault, isBankAcc, query]);

  // NO RESULT/RESULTS FOR SPECIFIC SEARCH

  const [visible, setVisible] = useState(true);
  const [prevScrollPos, setPrevScrollPos] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollPos = window.pageYOffset;

      setVisible(currentScrollPos < 100 || prevScrollPos > currentScrollPos);

      setPrevScrollPos(currentScrollPos);
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [prevScrollPos]);

  useEffect(() => {
    if (showDropdown) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [showDropdown]);

  return (
    <Stack
      flexDir="row"
      justifyContent="space-between"
      alignItems="center"
      h="64px"
      top={0}
      width="100%"
      bg="#BB5D82"
      zIndex={1000}
      opacity={visible ? 1 : 0}
      transform={`translateY(${visible ? "0" : "0"})`}
      transition="top 0.5s ease-in-out"
      position="fixed"
    >
      {!isSearchOpen ? (
        <Box pl="20px">
          <Link href="https://www.medsupplysolutions.com">
            <Logo
              textColor="#fff"
              dotsColor="#fff"
              color="#fff"
              width="133px !important"
              height="36px !important"
            />
          </Link>
        </Box>
      ) : (
        <Stack px="unset" w="100%" py="8px">
          <InputGroup position="relative">
            <InputLeftElement
              pointerEvents="none"
              color="gray.400"
              fontSize="lg"
              borderRadius="4px"
              ml="20px"
            >
              <MdSearch fill="#fff" color="#CB809E" />
            </InputLeftElement>
            <Input
              ml="20px"
              placeholder="Search medical products and supplies"
              rounded="md"
              textColor="#4A5568"
              borderColor="#A0AEC0"
              focusBorderColor="#BB5D82"
              width="100%"
              bg="#fff"
              onChange={handleInputChange}
              value={query}
            />
            {showDropdown && (
              <Stack
                pb="120px"
                ref={dropdownRef}
                position="absolute"
                top="100%" // Position the dropdown below the input
                left={0}
                bg="#fff"
                p={5}
                mt="8px"
                overflowY="scroll"
                // bg="#fff"
                width="100vw" // Full width of the viewport
                height="100vh" // Adjust as needed
                boxShadow="0 2px 4px rgba(0, 0, 0, 0.1)"
              >
                {loading ? (
                  <Center h="100vh">
                    {" "}
                    <Spinner alignItems="center" justifyContent="center" />
                  </Center>
                ) : (
                  <>
                    <VStack maxW="552px" zIndex={1000}>
                      {/* PRODUCTS */}
                      <NoResults
                        items={filteredProducts}
                        title="Products"
                        columns={{ lg: 1, xl: 2 }}
                        emptyMessage="Sorry, there are no matches for your search for this category."
                        emptyMessageLink={
                          <>
                            Try changing some of your filters to see product
                            results or{" "}
                            <Link
                              href="https://www.medsupplysolutions.com/shop/"
                              fontSize="14px"
                              fontWeight="500"
                              color="#BB5D82"
                              textDecoration="underline"
                            >
                              see all products.
                            </Link>
                          </>
                        }
                        mapFunction={(item: {
                          id: string;
                          slug: string;
                          name: string;
                          description: string;
                          databaseId: number;
                          featuredImage: any;
                          allPaActiveIngredient: any;
                          stockStatus: any;
                          isOnPromotion: any;
                          altText: string;
                        }) => {
                          const imageData =
                            item.featuredImage?.node?.localFile?.childImageSharp
                              ?.gatsbyImageData;
                          const altText = item.featuredImage?.node?.altText;
                          return (
                            <Link
                              style={{ textDecoration: "none" }}
                              href={`/product/${item.slug}/`}
                              key={item.id}
                            >
                              <ProductCard
                                heading="div"
                                height="320px"
                                width="300px"
                                border="1px solid #E1E7EF"
                                name={item.name}
                                activeSubstance={
                                  item.allPaActiveIngredient?.nodes
                                }
                                key={item.id}
                                productId={item.databaseId}
                                imageData={imageData}
                                altText={altText}
                                slug={item.slug}
                                isSearch
                                isRelatedProducts={true}
                                isProductOnPromotion={
                                  item?.isOnPromotion?.isonpromotion
                                }
                              />
                            </Link>
                          );
                        }}
                      />
                    </VStack>
                    <VStack mt="12px" alignItems="flex-start" gap="24px">
                      {/* BRANDS exist */}
                      <NoResults
                        isSearch
                        items={filteredBrands}
                        title="Brands"
                        emptyMessage="Sorry, there are no products that match your filter choices."
                        emptyMessageLink={
                          <>
                            Try changing some of your filters to see product
                            results or{" "}
                            <Link
                              href="https://www.medsupplysolutions.com/shop/"
                              fontSize="14px"
                              fontWeight="500"
                              color="#BB5D82"
                              textDecoration="underline"
                            >
                              see all products.
                            </Link>
                          </>
                        }
                        mapFunction={(item: { name: string; slug: string }) => (
                          <Link
                            key={item.slug}
                            href={`${item.slug}/`}
                            fontSize="16px"
                            fontWeight="500"
                            color="#1A212D"
                          >
                            {item.name.toUpperCase()}
                          </Link>
                        )}
                      />

                      {/* CATEGORIES exist */}
                      <NoResults
                        isSearch
                        items={filteredCategories}
                        title="Categories"
                        emptyMessage="Sorry, there are no products that match your filter choices."
                        emptyMessageLink={
                          <>
                            Try changing some of your filters to see product
                            results or{" "}
                            <Link
                              href="https://www.medsupplysolutions.com/shop/"
                              fontSize="14px"
                              fontWeight="500"
                              color="#BB5D82"
                              textDecoration="underline"
                            >
                              see all products.
                            </Link>
                          </>
                        }
                        mapFunction={(item: { name: string; slug: string }) => (
                          <Link
                            key={item.slug}
                            href={`/${item.slug}/`}
                            fontSize="16px"
                            fontWeight="500"
                            color="#1A212D"
                          >
                            {item.name}
                          </Link>
                        )}
                      />
                    </VStack>
                    <VStack pb="120px">
                      {/* POSTS exist  */}
                      <NoResults
                        items={postsData}
                        title="Articles"
                        emptyMessage="Sorry, there are no matches for your search for this category."
                        mapFunction={(item: {
                          title: string;
                          slug: string;
                          excerpt: string;
                        }) => (
                          <Box>
                            <Link
                              href={"/blog/" + item.slug}
                              fontSize="20px !important"
                              fontWeight="500 !important"
                              lineHeight="28px"
                              color="#171923"
                            >
                              <Box
                                dangerouslySetInnerHTML={{
                                  __html: String(item.title),
                                }}
                              />
                            </Link>
                            <Box
                              fontWeight="400 !important"
                              color="#4A5568 !important"
                              fontSize="14px !important"
                              lineHeight="19.6px"
                              noOfLines={3}
                              dangerouslySetInnerHTML={{
                                __html: String(item.excerpt),
                              }}
                            />
                          </Box>
                        )}
                      />
                    </VStack>
                  </>
                )}
              </Stack>
            )}
          </InputGroup>
        </Stack>
      )}

      <Stack flexDir="row" mr="20px">
        <IconButton
          _hover={{ bg: "blackAlpha.200" }}
          aria-label="Search database"
          variant="outline"
          icon={
            showDropdown ? (
              <MdClose fill="#fff" width="24px" height="24px" />
            ) : (
              <MdSearch color="#fff" width="24px" height="24px" />
            )
          }
          onClick={toggleSearch}
        />

        <IconButton
          _hover={{ bg: "blackAlpha.200" }}
          aria-label="vertical"
          variant="outline"
          icon={<HiDotsVertical color="#fff" width="24px" height="24px" />}
          onClick={
            mobileNavbar.isOpen ? mobileNavbar.onClose : mobileNavbar.onOpen
          }
        />
        <MobileDrawer
          isOpen={mobileNavbar.isOpen}
          onClose={mobileNavbar.onClose}
        />
      </Stack>
    </Stack>
  );
};

export default SmallResolutionHeader;
